import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import AISuggestReplies from '../components/social/AISuggestReplies';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { 
  Star,
  MessageSquare,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Reply,
  MoreHorizontal,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Link as LinkIcon,
  Loader2,
  X
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const ReviewsPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(null); // null=unknown, true/false=known
  const [locationSelected, setLocationSelected] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchConnection = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-business/connection`, getAuthHeaders());
      setGoogleConnected(!!res.data.connected);
      setLocationSelected(!!res.data.location_name);
    } catch (e) {
      setGoogleConnected(false);
    }
  }, [API, getAuthHeaders]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews`, getAuthHeaders());
      setReviews(response.data);
    } catch (error) {
      console.error('Errore nel caricamento recensioni:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchConnection();
    fetchReviews();
  }, [fetchConnection, fetchReviews]);

  // Allinea subito le recensioni all'attività collegata ora (silenzioso)
  useEffect(() => {
    if (!googleConnected || !locationSelected) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.post(`${API}/reviews/sync`, {}, getAuthHeaders());
        if (!active) return;
        setLastSyncedAt(res.data.last_synced_at);
        await fetchReviews();
      } catch (e) {
        /* sincronizzazione manuale sempre disponibile */
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConnected, locationSelected]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/reviews/sync`, {}, getAuthHeaders());
      setLastSyncedAt(res.data.last_synced_at);
      toast.success(`Sincronizzate ${res.data.synced} recensioni da Google`);
      await fetchReviews();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore nella sincronizzazione';
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedReview) return;
    setReplySubmitting(true);
    try {
      await axios.put(`${API}/reviews/${selectedReview.id}/reply`,
        { response: replyText },
        getAuthHeaders()
      );
      await fetchReviews();
      setIsDialogOpen(false);
      setReplyText('');
      setSelectedReview(null);
      toast.success('Risposta pubblicata su Google');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore nell\'invio risposta';
      toast.error(msg);
    } finally {
      setReplySubmitting(false);
    }
  };

  const openReplyDialog = (review) => {
    setSelectedReview(review);
    setReplyText(review.response || '');
    setIsDialogOpen(true);
  };

  const stats = {
    total: reviews.length,
    average: reviews.length ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : 0,
    pending: reviews.filter(r => !r.response).length,
    positive: reviews.filter(r => r.rating >= 4).length,
  };

  const visibleReviews = reviews.filter((r) => {
    if (filter === 'pending') return !r.response;
    if (filter === 'positive') return r.rating >= 4;
    return true;
  });

  const renderStars = (rating, size = 'w-4 h-4') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reviews-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Recensioni</h1>
          <p className="text-muted-foreground mt-1">
            Recensioni reali da Google Business Profile
            {lastSyncedAt && (
              <span className="ml-2 text-xs">
                · Aggiornate {formatDistanceToNow(parseISO(lastSyncedAt), { locale: it, addSuffix: true })}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {googleConnected && locationSelected && (
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing}
              className="flex-1 sm:flex-initial"
              data-testid="sync-reviews-btn"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sincronizzazione...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" />Sincronizza</>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/google-business')} className="flex-1 sm:flex-initial" data-testid="open-google-business-btn">
            <ExternalLink className="w-4 h-4 mr-2" />
            Google Business
          </Button>
        </div>
      </div>

      {/* Google connection banner */}
      {googleConnected === false && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-700 dark:text-amber-400">Google Business non collegato</p>
              <p className="text-sm text-muted-foreground mt-1">
                Collega il tuo profilo Google Business per visualizzare e rispondere alle recensioni reali dei tuoi clienti.
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/google-business')} data-testid="connect-google-btn">
              <LinkIcon className="w-4 h-4 mr-2" />Collega
            </Button>
          </CardContent>
        </Card>
      )}
      {googleConnected === true && !locationSelected && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-700 dark:text-amber-400">Seleziona una sede</p>
              <p className="text-sm text-muted-foreground mt-1">
                Hai collegato Google ma non hai ancora scelto la sede aziendale da gestire.
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/google-business')}>
              Seleziona sede
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Statistiche: ogni blocco filtra la lista */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key: 'all', value: stats.average, label: 'Media Valutazioni', tid: 'media', icon: Star,
            color: 'text-yellow-500 fill-yellow-500', bg: 'bg-yellow-500/10' },
          { key: 'all', value: stats.total, label: 'Recensioni Totali', tid: 'totali', icon: MessageSquare,
            color: 'text-blue-500', bg: 'bg-blue-500/10' },
          { key: 'pending', value: stats.pending, label: 'In Attesa di Risposta', tid: 'senza-risposta', icon: Clock,
            color: 'text-orange-500', bg: 'bg-orange-500/10' },
          { key: 'positive', value: stats.positive, label: 'Positive (4-5)', tid: 'positive', icon: ThumbsUp,
            color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
        ].map((c, i) => {
          const active = filter === c.key && !(c.key === 'all' && i === 0);
          return (
            <Card key={c.label}
              onClick={() => setFilter(c.key)}
              data-testid={`filter-${c.tid}`}
              aria-pressed={active}
              className={`card-hover cursor-pointer transition-colors ${active ? 'border-primary ring-1 ring-primary/40' : 'hover:border-primary/40'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center shrink-0`}>
                    <c.icon className={`w-5 h-5 ${c.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold font-outfit">{c.value}</p>
                    <p className="text-xs text-muted-foreground leading-tight">{c.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filter !== 'all' && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="active-filter-bar">
          <span className="text-sm text-muted-foreground">
            {filter === 'pending' ? 'Mostro solo le recensioni senza risposta' : 'Mostro solo le recensioni da 4 e 5 stelle'}
            {' '}({visibleReviews.length})
          </span>
          <Button size="sm" variant="ghost" onClick={() => setFilter('all')} data-testid="clear-filter">
            <X className="w-3.5 h-3.5 mr-1" />Mostra tutte
          </Button>
        </div>
      )}

      {/* Lista Recensioni */}
      {visibleReviews.length > 0 ? (
        <div className="space-y-4">
          {visibleReviews.map((review, index) => (
            <Card 
              key={review.id} 
              className="card-hover animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`review-${review.id}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                    {review.reviewer_photo_url && (
                      <AvatarImage src={review.reviewer_photo_url} alt={review.reviewer_name} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                      {(review.reviewer_name || '?').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <h3 className="font-semibold font-outfit break-words">{review.reviewer_name}</h3>
                      {renderStars(review.rating)}
                      <Badge variant="outline" className="capitalize">
                        {review.source === 'local' ? 'inserita a mano' : review.platform}
                      </Badge>
                      {!review.response && (
                        <Badge variant="secondary" className="bg-orange-500/10 text-orange-600">
                          Da Rispondere
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      {format(parseISO(review.created_at), 'd MMM yyyy', { locale: it })}
                    </p>
                  </div>
                </div>

                <p className="mt-3 sm:ml-16 text-[15px] leading-relaxed text-foreground whitespace-pre-line break-words"
                  data-testid={`review-content-${review.id}`}>
                  {review.content}
                </p>

                {review.response && (
                  <div className="mt-4 sm:ml-16 p-3 sm:p-4 bg-muted/50 rounded-lg border-l-2 border-primary"
                    data-testid={`review-response-${review.id}`}>
                    <p className="text-xs font-medium text-muted-foreground mb-1">La Tua Risposta</p>
                    <p className="text-sm leading-relaxed whitespace-pre-line break-words">{review.response}</p>
                  </div>
                )}

                <div className="mt-4 flex sm:justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto min-h-[44px]"
                    onClick={() => openReplyDialog(review)}
                    data-testid={`reply-btn-${review.id}`}
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    {review.response ? 'Modifica risposta' : 'Rispondi'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Star className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold font-outfit mb-1">
              {filter === 'pending' ? 'Hai risposto a tutte'
                : filter === 'positive' ? 'Nessuna recensione positiva'
                : 'Nessuna recensione'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {filter !== 'all'
                ? 'Nessuna recensione con questo filtro.'
                : googleConnected && locationSelected
                ? 'Le recensioni Google della tua attività appariranno qui non appena i clienti le pubblicheranno.'
                : 'Collega Google Business per iniziare a ricevere le recensioni dei tuoi clienti.'}
            </p>
            {filter !== 'all' && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setFilter('all')}
                data-testid="clear-filter-empty">Mostra tutte</Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Risposta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">Rispondi alla Recensione</DialogTitle>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-medium">{selectedReview.reviewer_name}</span>
                  {renderStars(selectedReview.rating, 'w-3.5 h-3.5')}
                </div>
                <p className="text-sm text-muted-foreground">{selectedReview.content}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reply">La Tua Risposta</Label>
                <Textarea
                  id="reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrivi una risposta professionale e utile..."
                  rows={4}
                  data-testid="review-reply-input"
                />
                <AISuggestReplies kind="review" text={selectedReview.content} author={selectedReview.reviewer_name} rating={selectedReview.rating} onPick={setReplyText} compact />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={replySubmitting}>Annulla</Button>
                <Button onClick={handleReply} disabled={replySubmitting || !replyText.trim()} data-testid="submit-reply-btn">
                  {replySubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio...</>) : 'Invia Risposta'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsPage;
